import { put, call, takeLatest, takeEvery } from 'redux-saga/effects';

import AuthUtil from '../../AuthUtil';
import { REAL_ESTATE } from '../../../components/global/Server/endpoints';
import { hideError, showErrorMessage } from '../../actions/global';
import {
    RE_LIST,
    RE_SAVE_HOUSE,
    RE_DELETE_HOUSE,
    RE_SAVE_PARTNER,
    RE_ADD_PARTNER,
    RE_DELETE_PARTNER,
    RE_SAVE_EXPENSE,
    RE_DELETE_EXPENSE,
} from '../../constants/realEstate';
import {
    listRealEstate,
    storeRealEstate,
    storeHouse,
    removeHouse,
    storePartner,
    removePartner,
} from '../../actions/realEstate';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../components/dashboard/constants';

// LIST — pulls partners + houses the user can see in one shot.
function* listSaga(action) {
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(REAL_ESTATE.LIST_ALL, action.data || {}),
        );
        if (response.status === 200 && response.data.success) {
            yield put(
                storeRealEstate({
                    partners: response.data.partners || [],
                    houses: response.data.houses || [],
                    addableUsers: response.data.addableUsers || [],
                    currentPersonId: response.data.currentPersonId || null,
                    role: response.data.role || null,
                    canSeeAll: !!response.data.canSeeAll,
                }),
            );
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    }
}

// Every house write returns { success, house } — the full updated ledger
// (expenses & settlement embedded). The reducer upserts it.
function* writeAndStoreHouse(endpoint, action) {
    yield put(hideError());
    try {
        const response = yield call(() => AuthUtil.postRequest(endpoint, action.data));
        if (response.status === 200 && response.data.success) {
            if (response.data.house) yield put(storeHouse(response.data.house));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

function* saveHouseSaga(action) { yield* writeAndStoreHouse(REAL_ESTATE.SAVE_HOUSE, action); }
function* saveExpenseSaga(action) { yield* writeAndStoreHouse(REAL_ESTATE.SAVE_EXPENSE, action); }
function* deleteExpenseSaga(action) { yield* writeAndStoreHouse(REAL_ESTATE.DELETE_EXPENSE, action); }

// DELETE HOUSE — removes from state instead of upserting.
function* deleteHouseSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() => AuthUtil.postRequest(REAL_ESTATE.DELETE_HOUSE, action.data));
        if (response.status === 200 && response.data.success) {
            yield put(removeHouse(response.data.houseId || response.data.id || action.data.id));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

// SAVE PARTNER — server returns { success, partner }; reducer upserts.
function* savePartnerSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() => AuthUtil.postRequest(REAL_ESTATE.SAVE_PARTNER, action.data));
        if (response.status === 200 && response.data.success) {
            if (response.data.partner) yield put(storePartner(response.data.partner));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

// ADD PARTNER — grants a user access; re-list to refresh the roster +
// the addable-users list (the granted user is no longer addable).
function* addPartnerSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() => AuthUtil.postRequest(REAL_ESTATE.ADD_PARTNER, action.data));
        if (response.status === 200 && response.data.success) {
            if (response.data.partner) yield put(storePartner(response.data.partner));
            yield put(listRealEstate({}));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

// DELETE PARTNER — server returns { success, personId }.
function* deletePartnerSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() => AuthUtil.postRequest(REAL_ESTATE.DELETE_PARTNER, action.data));
        if (response.status === 200 && response.data.success) {
            yield put(removePartner(response.data.personId || action.data.personId));
            yield put(listRealEstate({}));
            if (typeof action.callback === 'function') action.callback(null, response.data.message);
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') action.callback(message, null);
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
    }
}

export default function* realEstateSagas() {
    yield takeLatest(RE_LIST, listSaga);
    yield takeEvery(RE_SAVE_HOUSE, saveHouseSaga);
    yield takeEvery(RE_DELETE_HOUSE, deleteHouseSaga);
    yield takeEvery(RE_SAVE_PARTNER, savePartnerSaga);
    yield takeEvery(RE_ADD_PARTNER, addPartnerSaga);
    yield takeEvery(RE_DELETE_PARTNER, deletePartnerSaga);
    yield takeEvery(RE_SAVE_EXPENSE, saveExpenseSaga);
    yield takeEvery(RE_DELETE_EXPENSE, deleteExpenseSaga);
}
